import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, CalendarCheck } from "lucide-react";
import Logo from "./Logo";

const CALENDLY = "https://calendly.com/wordgumbolearningco";

const NAV = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Dyslexia Support", href: "#support" },
  { label: "Assessments", href: "#assessments" },
  { label: "Programs", href: "#programs" },
  { label: "Free Consultation", href: "#consultation" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-wg-purpleDeep/95 backdrop-blur-xl border-b border-white/10 shadow-[0_8px_30px_rgb(0,0,0,0.25)]"
          : "bg-wg-purple"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between h-20 md:h-24">
        <a href="#home" className="flex items-center group" data-testid="nav-logo-link" aria-label="Word Gumbo Learning Co. home">
          <Logo className="h-16 md:h-20 lg:h-24 w-auto transition-transform group-hover:rotate-[-3deg]" />
        </a>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-7 ml-auto xl:mr-8" aria-label="Primary">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              className="wg-link whitespace-nowrap font-medium text-white/85 hover:text-wg-yellow transition-colors text-[15px]"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={CALENDLY}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-schedule-consultation-btn"
            className="hidden md:inline-flex whitespace-nowrap items-center gap-2 bg-white text-wg-purple rounded-full px-5 lg:px-6 py-3 font-semibold text-sm hover:bg-wg-yellow hover:text-wg-ink hover:-translate-y-0.5 shadow-lg shadow-black/20 transition-all"
          >
            <CalendarCheck className="w-4 h-4" />
            Schedule Free Consultation
          </a>

          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((s) => !s)}
            className="xl:hidden inline-flex items-center justify-center h-11 w-11 rounded-full bg-white text-wg-purple shadow-sm"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-nav"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="xl:hidden bg-wg-purpleDeep/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                  className="py-3 px-2 font-medium text-white/90 hover:text-wg-yellow rounded-xl hover:bg-white/5"
                >
                  {n.label}
                </a>
              ))}
              <a
                href={CALENDLY}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="mobile-schedule-consultation-btn"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-white text-wg-purple rounded-full px-5 py-3 font-semibold text-sm shadow-lg"
              >
                <CalendarCheck className="w-4 h-4" />
                Schedule Free Consultation
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
